import { parseVideoUrl } from "@/helpers/parseVideoUrl"

function normalizeUrl(url: string) {
  return url.trim().replace(/[),.;!?]+$/g, "")
}

function getUrlsFromText(text: string) {
  const matches = text.match(/https?:\/\/[^\s<>"']+/gi) ?? []
  return matches.map(normalizeUrl)
}

export function extractVideoUrls(content = "") {
  if (!content.trim()) return []

  const urls = new Map<string, string>()

  const addVideoUrl = (url: string) => {
    const normalizedUrl = normalizeUrl(url)
    const { provider, embedId } = parseVideoUrl(normalizedUrl)

    if (!provider || !embedId) {
      return
    }

    const key = `${provider}:${embedId}`

    if (!urls.has(key)) {
      urls.set(key, normalizedUrl)
    }
  }

  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(content, "text/html")

    doc.querySelectorAll("a[href]").forEach(link => {
      const linkTextUrl = getUrlsFromText(link.textContent ?? "")[0]
      const href = (link as HTMLAnchorElement).href || link.getAttribute("href")

      if (linkTextUrl) {
        addVideoUrl(linkTextUrl)
        return
      }

      if (href) {
        addVideoUrl(href)
      }
    })

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
    const textNodes: string[] = []

    while (walker.nextNode()) {
      const currentNode = walker.currentNode as Text
      const parentElement = currentNode.parentElement

      if (parentElement?.closest("a")) {
        continue
      }

      textNodes.push(currentNode.nodeValue ?? "")
    }

    getUrlsFromText(textNodes.join(" ")).forEach(addVideoUrl)
  } else {
    getUrlsFromText(content).forEach(addVideoUrl)
  }

  return [...urls.values()]
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

export function removeVideoUrlsFromContent(content = "") {
  const videoUrls = extractVideoUrls(content)
  if (!videoUrls.length) return content

  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(content, "text/html")

    doc.querySelectorAll("a[href]").forEach(link => {
      const href = link.getAttribute("href")
      if (href && parseVideoUrl(href).provider) {
        link.remove()
      }
    })

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
    const textNodes: Text[] = []

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text)
    }

    textNodes.forEach(node => {
      let nextValue = node.nodeValue ?? ""

      videoUrls.forEach(url => {
        nextValue = nextValue.replace(new RegExp(escapeRegExp(url), "gi"), "")
      })

      nextValue = nextValue.replace(/\s{2,}/g, " ").trim()
      node.nodeValue = nextValue
    })

    doc.body.querySelectorAll("p, div").forEach(element => {
      const hasMedia = element.querySelector("img, video, iframe, a")
      const text = element.textContent?.trim() ?? ""

      if (!hasMedia && !text) {
        element.remove()
      }
    })

    return doc.body.innerHTML
  }

  let nextContent = content

  videoUrls.forEach(url => {
    nextContent = nextContent.replace(new RegExp(escapeRegExp(url), "gi"), "")
  })

  return nextContent.replace(/\s{2,}/g, " ").trim()
}
